export class Vector {
  constructor(public x: number, public y: number) {}

  add(v: Vector): Vector {
    return new Vector(this.x + v.x, this.y + v.y);
  }

  subtract(v: Vector): Vector {
    return new Vector(this.x - v.x, this.y - v.y);
  }

  multiply(n: number): Vector {
    return new Vector(this.x * n, this.y * n);
  }

  divide(n: number): Vector {
    return new Vector(this.x / n, this.y / n);
  }

  magnitude(): number {
    return Math.sqrt(this.x * this.x + this.y * this.y);
  }

  normal(): Vector {
    return new Vector(-this.y, this.x);
  }

  normalize(): Vector {
    const m = this.magnitude();
    return m > 0 ? this.divide(m) : new Vector(0, 0);
  }
}

export class Node {
  public id: string;
  public data: any;

  constructor(id: string, data: any = {}) {
    this.id = id;
    this.data = data;
  }
}

export class Edge {
  public id: string;
  public source: Node;
  public target: Node;
  public data: any;

  constructor(id: string, source: Node, target: Node, data: any = {}) {
    this.id = id;
    this.source = source;
    this.target = target;
    this.data = data;
  }
}

export class Graph {
  private nodes: Map<string, Node> = new Map();
  private edges: Edge[] = [];
  private nextNodeId = 0;
  private nextEdgeId = 0;

  addNode(node: Node): Node {
    this.nodes.set(node.id, node);
    return node;
  }

  newNode(data: any = {}): Node {
    const node = new Node(`node${this.nextNodeId++}`, data);
    return this.addNode(node);
  }

  addEdge(edge: Edge): Edge {
    if (!this.nodes.has(edge.source.id) || !this.nodes.has(edge.target.id)) {
      throw new Error('Can only add edges between nodes in the graph');
    }
    this.edges.push(edge);
    return edge;
  }

  newEdge(source: Node, target: Node, data: any = {}): Edge {
    const edge = new Edge(`edge${this.nextEdgeId++}`, source, target, data);
    return this.addEdge(edge);
  }

  getNodes(): Node[] {
    return Array.from(this.nodes.values());
  }

  getEdges(): Edge[] {
    return this.edges;
  }

  adjacentNodes(node: Node): Node[] {
    return this.edges
      .filter(e => e.source.id === node.id || e.target.id === node.id)
      .map(e => e.source.id === node.id ? e.target : e.source);
  }
}

interface LayoutPoint {
  p: Vector; // position
  v: Vector; // velocity
  m: number; // mass
}

export class ForceDirectedLayout {
  private graph: Graph;
  private stiffness: number;
  private repulsion: number;
  private damping: number;
  private minEnergyThreshold: number;
  private nodePoints: Map<string, LayoutPoint> = new Map();
  private edgeSprings: Map<Edge, { length: number }> = new Map();

  constructor(
    graph: Graph,
    stiffness = 400.0,
    repulsion = 400.0,
    damping = 0.5,
    minEnergyThreshold = 0.01
  ) {
    this.graph = graph;
    this.stiffness = stiffness;
    this.repulsion = repulsion;
    this.damping = damping;
    this.minEnergyThreshold = minEnergyThreshold;

    // Initialize node positions
    this.graph.getNodes().forEach(node => {
      this.nodePoints.set(node.id, {
        p: new Vector(Math.random() * 10 - 5, Math.random() * 10 - 5),
        v: new Vector(0, 0),
        m: 1.0
      });
    });

    // Initialize edge springs
    this.graph.getEdges().forEach(edge => {
      this.edgeSprings.set(edge, { length: 1.0 });
    });
  }

  tick(timestep: number = 0.03): void {
    // Apply spring forces
    this.applySprings();
    
    // Apply repulsive forces
    this.applyRepulsion();
    
    // Update positions
    this.updatePositions(timestep);
  }

  private applySprings(): void {
    this.graph.getEdges().forEach(edge => {
      const spring = this.edgeSprings.get(edge)!;
      const p1 = this.nodePoints.get(edge.source.id)!;
      const p2 = this.nodePoints.get(edge.target.id)!;

      const d = p2.p.subtract(p1.p);
      const displacement = d.magnitude() - spring.length;
      const direction = d.normalize();

      const force = direction.multiply(this.stiffness * displacement * 0.5);

      p1.v = p1.v.add(force.divide(p1.m));
      p2.v = p2.v.subtract(force.divide(p2.m));
    });
  }

  private applyRepulsion(): void {
    const nodes = this.graph.getNodes();
    
    for (let i = 0; i < nodes.length; i++) {
      for (let j = i + 1; j < nodes.length; j++) {
        const p1 = this.nodePoints.get(nodes[i].id)!;
        const p2 = this.nodePoints.get(nodes[j].id)!;

        const d = p2.p.subtract(p1.p);
        const distance = d.magnitude() + 0.1; // Avoid division by zero
        const direction = d.normalize();

        const force = direction.multiply(this.repulsion / (distance * distance * 0.5));

        p1.v = p1.v.subtract(force.divide(p1.m));
        p2.v = p2.v.add(force.divide(p2.m));
      }
    }
  }

  private updatePositions(timestep: number): void {
    this.nodePoints.forEach(point => {
      point.v = point.v.multiply(this.damping);
      point.p = point.p.add(point.v.multiply(timestep));
    });
  }

  totalEnergy(): number {
    let energy = 0;
    this.nodePoints.forEach(point => {
      const speed = point.v.magnitude();
      energy += 0.5 * point.m * speed * speed;
    });
    return energy;
  }

  getNodePosition(node: Node): Vector | undefined {
    return this.nodePoints.get(node.id)?.p;
  }

  eachNode(callback: (node: Node, point: Vector) => void): void {
    this.graph.getNodes().forEach(node => {
      const point = this.nodePoints.get(node.id);
      if (point) {
        callback(node, point.p);
      }
    });
  }

  eachEdge(callback: (edge: Edge) => void): void {
    this.graph.getEdges().forEach(callback);
  }
}

// Export a Springy-compatible object
export default {
  Graph,
  Node,
  Edge,
  Vector,
  Layout: {
    ForceDirected: ForceDirectedLayout
  }
};